// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle, XCircle, ArrowRight, Check, X } from 'lucide-react';
import { useTenantConfig } from '../../contexts/TenantContext';
import './NetProfitBreakdown.css';

const SECTION_SIGNS = {
  revenue: 1, cogs: -1, opex: -1, other_income: 1,
  other_expenses: -1, amortization: -1, depreciation: -1, interest: -1, tax: -1,
};

const SECTION_LABELS = {
  revenue: 'Revenue',
  cogs: 'Cost of Sales',
  opex: 'Operating Expenses',
  other_income: 'Other Income',
  other_expenses: 'Other Expenses',
  depreciation: 'Depreciation',
  amortization: 'Amortization',
  interest: 'Interest',
  tax: 'Tax',
};

const SECTION_ORDER = [
  'revenue', 'cogs', 'opex', 'other_income', 'other_expenses',
  'depreciation', 'amortization', 'interest', 'tax',
];

const fmt = (num) => {
  if (num == null || isNaN(num)) return '—';
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return num < 0 ? `(${formatted})` : formatted;
};

const fmtSign = (num) => {
  if (num == null || isNaN(num)) return '—';
  if (num === 0 || Math.abs(num) < 0.005) return '0.00';
  const prefix = num > 0 ? '+' : '';
  return `${prefix}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const NetProfitBreakdown = ({
  periodBreakdowns = [],
  editedAccounts = [],
  categoryToSection = {},
  suggestions = [],
  onApproveSuggestion,
  onRejectSuggestion,
  approvedSuggestions = new Set(),
  rejectedSuggestions = new Set(),
  balancingPlanIds = new Set(),
  selectedTenantConfig = null,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedPeriodIdx, setSelectedPeriodIdx] = useState(0);
  const [expandedSuggKey, setExpandedSuggKey] = useState(null);
  const { getCategoryColor: getTenantCategoryColor } = useTenantConfig();

  const getCategoryStyle = (category) => {
    const selectedConfigColors = selectedTenantConfig?.frontend_config?.categoryColors;
    let colorValue = selectedConfigColors?.[category];

    if (!colorValue) {
      const tenantColor = getTenantCategoryColor(category);
      colorValue = (tenantColor && tenantColor !== 'custom-category') ? tenantColor : 'custom-category';
    }

    const isRawColor = colorValue && (
      colorValue.startsWith('#') || colorValue.startsWith('rgb') || colorValue.startsWith('hsl')
    );

    if (isRawColor) {
      return {
        className: '',
        style: {
          background: `linear-gradient(135deg, ${colorValue}, ${colorValue}dd)`,
          color: '#ffffff',
        },
      };
    }

    return { className: colorValue, style: {} };
  };

  // Live recalculation from editedAccounts
  const liveBreakdowns = useMemo(() => {
    if (!periodBreakdowns.length || !editedAccounts.length) return periodBreakdowns;

    return periodBreakdowns.map(pb => {
      const periodAccounts = editedAccounts.filter(
        acc => acc.period_index === pb.period_index
      );

      if (periodAccounts.length === 0) return pb;

      const rollups = {};
      for (const section of SECTION_ORDER) {
        rollups[section] = { total: 0, count: 0 };
      }

      for (const acc of periodAccounts) {
        const cat = acc.standardized_category;
        if (!cat) continue;
        const catLower = typeof cat === 'string' ? cat.toLowerCase() : '';
        if (catLower.includes('net income') || catLower.includes('uncategorized')) continue;

        const section = categoryToSection[cat];
        if (!section || !rollups[section]) continue;

        rollups[section].total += parseFloat(acc.value) || 0;
        rollups[section].count += 1;
      }

      let mappedNI = 0;
      const sectionRollups = SECTION_ORDER.map(section => {
        const data = rollups[section];
        const sign = SECTION_SIGNS[section] || 0;
        const contribution = sign * data.total;
        mappedNI += contribution;
        return {
          section,
          sign,
          total: data.total,
          contribution,
          account_count: data.count,
        };
      });

      const discrepancy = pb.benchmark_value != null ? mappedNI - pb.benchmark_value : null;
      const passes = discrepancy != null ? Math.abs(discrepancy) <= 1 : true;

      return {
        ...pb,
        section_rollups: sectionRollups,
        mapped_net_income: mappedNI,
        discrepancy,
        passes,
      };
    });
  }, [editedAccounts, periodBreakdowns, categoryToSection]);

  if (!liveBreakdowns.length) return null;

  const currentBreakdown = liveBreakdowns[selectedPeriodIdx] || liveBreakdowns[0];
  const failCount = liveBreakdowns.filter(b => !b.passes).length;
  const passCount = liveBreakdowns.length - failCount;

  const dedupedSuggestions = suggestions
    .filter((s, idx, arr) => arr.findIndex(c => c.original_name === s.original_name) === idx);

  const accountsInPeriod = (() => {
    const names = new Set();
    for (const a of (currentBreakdown?.accounts || [])) {
      if (a?.original_name) names.add(a.original_name);
    }
    for (const r of (currentBreakdown?.section_rollups || [])) {
      for (const a of (r.accounts || [])) {
        if (a?.original_name) names.add(a.original_name);
      }
    }
    for (const acc of editedAccounts) {
      if (acc.period_index === currentBreakdown?.period_index && acc.original_name) {
        names.add(acc.original_name);
      }
    }
    return names;
  })();

  return (
    <div className="npc-breakdown" id="net-profit-breakdown">
      <div className="npc-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="npc-header-left">
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          <h4>Net Profit Breakdown</h4>
          <span className="npc-status-summary">
            {passCount > 0 && (
              <span className="npc-pass-count">
                <CheckCircle size={12} /> {passCount} pass
              </span>
            )}
            {failCount > 0 && (
              <span className="npc-fail-count">
                <XCircle size={12} /> {failCount} fail
              </span>
            )}
          </span>
        </div>
      </div>

      {!isCollapsed && (
        <div className="npc-content">
          <div className="npc-layout">
            {/* Period rail */}
            {liveBreakdowns.length > 1 && (
              <div className="npc-period-rail">
                {liveBreakdowns.map((pb, idx) => (
                  <button
                    key={idx}
                    className={`npc-period-tab ${idx === selectedPeriodIdx ? 'active' : ''} ${pb.passes ? 'pass' : 'fail'}`}
                    onClick={() => setSelectedPeriodIdx(idx)}
                  >
                    {pb.period_display}
                    {pb.passes
                      ? <CheckCircle size={10} />
                      : <XCircle size={10} />
                    }
                  </button>
                ))}
              </div>
            )}

            <div className="npc-main">

          {/* Section rollup table */}
          <table className="npc-section-table">
            <thead>
              <tr>
                <th></th>
                <th>Section</th>
                <th className="npc-num">Total</th>
                <th className="npc-num">NI Impact</th>
              </tr>
            </thead>
            <tbody>
              {(currentBreakdown.section_rollups || [])
                .filter(r => r.section !== 'unassigned')
                .map((rollup, idx) => {
                  const signChar = rollup.sign > 0 ? '+' : '−';
                  return (
                    <tr key={idx} className={`npc-row ${rollup.contribution > 0 ? 'positive' : rollup.contribution < 0 ? 'negative' : ''}`}>
                      <td className="npc-sign">{signChar}</td>
                      <td className="npc-section-name">
                        {SECTION_LABELS[rollup.section] || rollup.section}
                        <span className="npc-account-count">({rollup.account_count})</span>
                      </td>
                      <td className="npc-num">{fmt(rollup.total)}</td>
                      <td className="npc-num">{fmtSign(rollup.contribution)}</td>
                    </tr>
                  );
                })}
            </tbody>
            <tfoot>
              <tr className="npc-total-row">
                <td>=</td>
                <td>Mapped Net Income</td>
                <td className="npc-num" colSpan={2}>{fmt(currentBreakdown.mapped_net_income)}</td>
              </tr>
              {currentBreakdown.benchmark_value != null && (
                <>
                  <tr className="npc-benchmark-row">
                    <td></td>
                    <td>Original ({currentBreakdown.benchmark_name || 'Net Income'})</td>
                    <td className="npc-num" colSpan={2}>{fmt(currentBreakdown.benchmark_value)}</td>
                  </tr>
                  <tr className={`npc-discrepancy-row ${currentBreakdown.passes ? 'pass' : 'fail'}`}>
                    <td></td>
                    <td>
                      Discrepancy
                      <span className={`npc-badge ${currentBreakdown.passes ? 'pass' : 'fail'}`}>
                        {currentBreakdown.passes ? 'PASS' : 'FAIL'}
                      </span>
                    </td>
                    <td className="npc-num" colSpan={2}>{fmt(currentBreakdown.discrepancy)}</td>
                  </tr>
                </>
              )}
            </tfoot>
          </table>

          {/* Unassigned accounts warning */}
          {(currentBreakdown.section_rollups || [])
            .filter(r => r.section === 'unassigned' && r.account_count > 0)
            .map((unassigned, idx) => (
              <div key={idx} className="npc-unassigned-warning">
                {unassigned.account_count} unassigned account(s) totaling {fmt(unassigned.total)} — not included in NI calculation
              </div>
            ))}
            </div>

            {/* Suggestion rail */}
            <div className="npc-suggestion-rail">
              <div className="npc-rail-header">
                Suggestions
                {dedupedSuggestions.length > 0 && (
                  <span className="npc-rail-count">{dedupedSuggestions.length}</span>
                )}
              </div>
              {dedupedSuggestions.length === 0 ? (
                <div className="npc-no-suggestions">
                  <CheckCircle size={14} />
                  <span>No corrections needed</span>
                </div>
              ) : (
                <div className="npc-sugg-list">
                  {dedupedSuggestions.map((suggestion) => {
                    const key = `${suggestion.original_name}__${suggestion.original_row_number}`;
                    const isApproved = approvedSuggestions.has(key);
                    const isRejected = rejectedSuggestions.has(key);
                    const isExpanded = expandedSuggKey === key;
                    const inPeriod = accountsInPeriod.has(suggestion.original_name);
                    const status = isApproved ? 'approved' : isRejected ? 'rejected' : 'pending';
                    const oldStyle = getCategoryStyle(suggestion.current_category);
                    const newStyle = getCategoryStyle(suggestion.suggested_category);
                    const valueStr = suggestion.value != null
                      ? parseFloat(suggestion.value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                      : '';
                    const impactStr = suggestion.impact_on_net_income != null
                      ? `${suggestion.impact_on_net_income >= 0 ? '+' : ''}${suggestion.impact_on_net_income.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`
                      : '';

                    const inPlan = balancingPlanIds.has(key);

                    return (
                      <div
                        key={key}
                        className={`npc-sugg-chip ${status} ${isExpanded ? 'expanded' : ''} ${inPeriod ? 'in-period' : ''} ${inPlan ? 'in-plan' : ''}`}
                      >
                        <button
                          type="button"
                          className="npc-sugg-chip-head"
                          onClick={() => setExpandedSuggKey(isExpanded ? null : key)}
                        >
                          {isExpanded
                            ? <ChevronDown size={12} className="npc-sugg-chevron" />
                            : <ChevronRight size={12} className="npc-sugg-chevron" />}
                          <span className="npc-sugg-name" title={suggestion.original_name}>
                            {suggestion.original_name}
                          </span>
                          {valueStr && <span className="npc-sugg-value">{valueStr}</span>}
                          <span className={`npc-sugg-dot ${status}`} />
                        </button>

                        {isExpanded && (
                          <div className="npc-sugg-body">
                            <div className="npc-sugg-categories">
                              <span className={`category-badge ${oldStyle.className} old`} style={oldStyle.style}>
                                {suggestion.current_category}
                              </span>
                              <ArrowRight size={12} className="npc-sugg-arrow" />
                              <span className={`category-badge ${newStyle.className}`} style={newStyle.style}>
                                {suggestion.suggested_category}
                              </span>
                            </div>
                            {suggestion.reasoning && (
                              <p className="npc-sugg-reasoning">{suggestion.reasoning}</p>
                            )}
                            <div className="npc-sugg-footer">
                              <div className="npc-sugg-meta">
                                {impactStr && (
                                  <span className={`npc-impact ${suggestion.impact_on_net_income >= 0 ? 'positive' : 'negative'}`}>
                                    NI Impact: {impactStr}
                                  </span>
                                )}
                                {suggestion.confidence && (
                                  <span className={`npc-confidence ${suggestion.confidence}`}>
                                    {suggestion.confidence}
                                  </span>
                                )}
                              </div>
                              <div className="npc-sugg-actions">
                                {isApproved ? (
                                  <span className="npc-sugg-status approved">
                                    <Check size={12} /> Approved
                                  </span>
                                ) : isRejected ? (
                                  <span className="npc-sugg-status rejected">
                                    <X size={12} /> Rejected
                                  </span>
                                ) : (
                                  <>
                                    <button
                                      className="npc-btn-approve"
                                      onClick={() => onApproveSuggestion?.(suggestion)}
                                    >
                                      <Check size={12} /> Approve
                                    </button>
                                    <button
                                      className="npc-btn-reject"
                                      onClick={() => onRejectSuggestion?.(suggestion)}
                                    >
                                      <X size={12} /> Reject
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetProfitBreakdown;
